/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React from "react";
import divorceCostsStyle from "./divorceCostsStyle";
import Grid from "@mui/material/Grid";
import CostCalculator from "../../../../services/divorce/CostCalculator";
import FeesService from "../../../../services/FeesService";
import { Divider, Typography } from "@mui/material";
import { AbstractCase } from "../../../../types/Entities/AbstractCase";
import { PARTNER_IRIS } from "../../../../constants/partnerIris";

type DivorceCostsProps = {
  divorce: AbstractCase;
};

const DivorceCosts = ({ divorce }: DivorceCostsProps) => {
  const valueInDispute = CostCalculator.calculateValueInDispute(divorce);
  const installmentRate = (FeesService.getExpenses(true) + CostCalculator.getHearingCost(valueInDispute) + CostCalculator.getProcessCost(valueInDispute)) / 10;

  const CostText = ({ divorce }: { divorce: AbstractCase }) => {
    switch (true) {
      case !!divorce.hasProcessCostSupport:
        return (
          <Grid sx={divorceCostsStyle.postfix} size={12}>
            <strong>Sie müssen voraussichtlich nichts zahlen!</strong>
            <br />
            Wenn Ihr Antrag auf Verfahrenskostenhilfe bewilligt worden ist, sind alle Zahlungen über die Verfahrenskostenhilfe abgedeckt.
          </Grid>
        );

      case !!divorce.installment:
        return (
          <Grid sx={divorceCostsStyle.hintBox} size={12}>
            <strong>Sie zahlen bequem per Raten!</strong>
            <br />
            <p>
              Sie haben sich für eine Ratenzahlung entscheiden. Sie zahlen daher die Kosten bequem über die nächsten 10 Monate in 10 Raten zu je{" "}
              {(Math.round(installmentRate * 100) / 100).toLocaleString("de-DE")}€.
            </p>
          </Grid>
        );
      case divorce.acquisitionPartner === PARTNER_IRIS.ADVOCARD && divorce.paymentType === "insurance":
        return (
          <Grid sx={divorceCostsStyle.postfix} size={12}>
            <strong>Sie sind bei der Advocard versichert?</strong>
            <br />
            Dann übernimmt Ihre Versicherung einen Teil der Kosten für Sie! Kommen Sie bei Fragen hierzu gerne auf uns zu.
          </Grid>
        );
      default:
        return (
          <Grid sx={divorceCostsStyle.postfix} size={12}>
            <strong>Fragen zur Finanzierung?</strong>
            <br />
            Melden Sie sich dazu gerne direkt bei uns!
          </Grid>
        );
    }
  };

  const costsSum = divorce.sumOfAllFees + divorce.courtFee;

  return (
    <>
      <Typography
        sx={{
          textAlign: { xs: "center", md: "left" },
          paddingBottom: "20px",
          marginBottom: "16px",
        }}
      >
        {divorce.paymentType !== "insurance"
          ? "Die Höhe der Scheidungskosten kann, basierend auf Ihren Angaben, wie folgt geschätzt werden:"
          : `Die Höhe der Gerichtskosten kann, basierend auf Ihren Angaben, auf ${divorce.courtFee.toLocaleString("de-DE", { style: "currency", currency: "EUR" })} geschätzt werden.`}
      </Typography>
      {divorce.paymentType !== "insurance" && (
        <Grid container sx={divorceCostsStyle.divorceCosts}>
          <Grid size={8}>Verfahrensgebühr</Grid>
          <Grid sx={divorceCostsStyle.money} size={4}>
            {divorce.processFeeAndExpenses.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
          </Grid>
          <Grid size={8}>Terminsgebühr</Grid>
          <Grid sx={divorceCostsStyle.money} size={4}>
            {divorce.hearingFee.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
          </Grid>
          <Grid size={8}>Gerichtskosten</Grid>
          <Grid sx={divorceCostsStyle.money} size={4}>
            {divorce.courtFee.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
          </Grid>
          <Grid size={12}>
            <Divider sx={divorceCostsStyle.divider} />
          </Grid>
          <Grid sx={divorceCostsStyle.sum} size={8}>
            Scheidungskosten insgesamt
          </Grid>
          <Grid sx={[divorceCostsStyle.sum, divorceCostsStyle.money]} size={4}>
            {costsSum.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
          </Grid>
          <CostText divorce={divorce} />
        </Grid>
      )}
    </>
  );
};

export default DivorceCosts;
